// src/components/tabs/MatchingTab.jsx
import React, { useMemo } from 'react';
import { FileText, CreditCard, BarChart3, Target } from 'lucide-react';
import Hero from '../shared/Hero';

const MatchingTab = ({ invoices, transactions, stats, formatCurrency }) => {
  const unmatchedInvoices = useMemo(
    () => invoices.filter((inv) => !inv.matched_transaction_id),
    [invoices]
  );
  const unmatchedTransactions = useMemo(
    () => transactions.filter((t) => !t.matched_invoice_id && t.transaction_amount < 0),
    [transactions]
  );

  return (
    <div>
      <Hero
        title="Matching Automático"
        subtitle="Sugestões de reconciliação com IA"
        icons={['/icons/document-eu.png', '/icons/globe.png']}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Unmatched Invoices */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" />
              Unmatched Invoices
            </h3>
            <p className="text-gray-600 text-sm mt-1">{unmatchedInvoices.length} invoices need matching</p>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {unmatchedInvoices.map((invoice) => (
              <div
                key={invoice.id}
                className="p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{invoice.supplier_name}</p>
                    <p className="text-sm text-gray-600">{invoice.invoice_date}</p>
                    <p className="text-sm font-medium text-purple-600">
                      {formatCurrency(invoice.total_amount, invoice.currency)}
                    </p>
                  </div>
                  <button
                    className="px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm hover:bg-green-200 transition-colors font-medium flex items-center gap-1"
                  >
                    <Target className="w-4 h-4" />
                    Find Match
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Unmatched Transactions */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Unmatched Expenses
            </h3>
            <p className="text-gray-600 text-sm mt-1">{unmatchedTransactions.length} expenses need matching</p>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {unmatchedTransactions.map((transaction) => (
              <div
                key={transaction.id}
                className="p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 text-sm">
                      {transaction.remittance_information}
                    </p>
                    <p className="text-sm text-gray-600">{transaction.booking_date}</p>
                    <p className="text-sm font-medium text-red-600">
                      {formatCurrency(transaction.transaction_amount, transaction.currency)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Matching Statistics */}
      <div className="bg-white rounded-xl p-6 border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-purple-600" />
          Matching Statistics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
            <div className="text-3xl font-bold text-green-700">{stats.matched}</div>
            <div className="text-sm text-green-600 font-medium">Matched Invoices</div>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-lg">
            <div className="text-3xl font-bold text-red-700">{stats.unmatched}</div>
            <div className="text-sm text-red-600 font-medium">Unmatched Invoices</div>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
            <div className="text-3xl font-bold text-purple-700">{stats.matchRate}%</div>
            <div className="text-sm text-purple-600 font-medium">Match Rate</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchingTab;
